import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MaskLines } from "../lib/motion";
import { IMAGES } from "../data";

export default function Hero({ ready, onQuote }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const overlay = useTransform(scrollYProgress, [0, 1], [0.35, 0.7]);

  return (
    <section ref={ref} id="top" className="relative h-[100svh] min-h-[620px] overflow-hidden">
      {/* Parallax + slow cinematic zoom */}
      <motion.div className="absolute inset-0" style={{ y, scale }}>
        <motion.img
          src={IMAGES.hero}
          alt="International container port at sunrise"
          className="w-full h-[120%] object-cover"
          initial={{ scale: 1.15 }}
          animate={{ scale: 1 }}
          transition={{ duration: 12, ease: "easeOut" }}
        />
      </motion.div>
      <motion.div className="absolute inset-0 bg-black" style={{ opacity: overlay }} />
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/30" />

      <div className="relative h-full max-w-[1400px] mx-auto px-6 md:px-10 flex flex-col justify-center">
        <motion.p
          className="overline text-[#B08D57] mb-6"
          initial={{ opacity: 0 }}
          animate={ready ? { opacity: 1 } : {}}
          transition={{ duration: 0.8, delay: 0.3 }}
          data-testid="hero-label"
        >
          Premium Indian Export House
        </motion.p>

        <h1 className="hero-title font-display text-[#F8F7F3] max-w-[820px]" data-testid="hero-headline">
          <MaskLines lines={["India's Finest.", "Trusted Worldwide."]} active={ready} delay={0.4} />
        </h1>

        <motion.p
          className="font-body text-[#EDE8DF] text-lg md:text-xl max-w-[620px] mt-8 leading-relaxed"
          initial={{ opacity: 0, y: 20 }}
          animate={ready ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.9, delay: 0.9, ease: [0.16, 1, 0.3, 1] }}
        >
          AGRAGA Velora Exports connects global buyers with India&apos;s finest products through
          uncompromising quality, transparent partnerships, and dependable execution.
        </motion.p>

        <motion.div
          className="flex flex-col sm:flex-row items-start sm:items-center gap-6 mt-10"
          initial={{ opacity: 0, y: 20 }}
          animate={ready ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.9, delay: 1.1, ease: [0.16, 1, 0.3, 1] }}
        >
          <button
            data-testid="hero-quote-btn"
            onClick={onQuote}
            className="w-full sm:w-auto rounded-full bg-[#111] text-white font-head font-semibold px-9 py-4 transition-colors duration-300 hover:bg-[#B08D57]"
          >
            Request a Quote
          </button>
          <button
            data-testid="hero-explore-btn"
            onClick={() => document.getElementById("products")?.scrollIntoView({ behavior: "smooth" })}
            className="link-underline text-[#F8F7F3] font-body text-base"
          >
            Explore Products
          </button>
        </motion.div>
      </div>

      {/* scroll cue */}
      <motion.div
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
        initial={{ opacity: 0 }}
        animate={ready ? { opacity: 1 } : {}}
        transition={{ delay: 1.4, duration: 1 }}
      >
        <span className="overline text-white/60 text-[10px]">Scroll</span>
        <motion.div
          className="w-px h-10 bg-white/40"
          animate={{ scaleY: [0.3, 1, 0.3] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          style={{ transformOrigin: "top" }}
        />
      </motion.div>
    </section>
  );
}
